import React, { useContext, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
    ASSESSMENT_BY_ID,
    ASSESSMENTS_MINIMAL,
    SEND_ASSESSMENT_UPDATE,
    TAG_SUGGESTIONS
} from '../../apollo/queries';
import moment from 'moment/moment';
import styles from './RequestProgressScanForm.module.scss';
import Select from 'react-select';
import * as yup from 'yup';
import { Field, Formik, FormikProps } from 'formik';
import { Input } from '../common/Input';
import { ButtonPageLayout } from '../common/ButtonPageLayout';
import { RequestAssessmentUpdateData } from '../../apollo/types/RequestAssessmentUpdateData';
import { AssessmentRoute } from '../../apollo/types/AssessmentRoute';
import { useLazyQuery } from '@apollo/react-hooks';
import { UserContext } from '../../context/UserContext';
import { AssessmentInviteData } from '../../apollo/types/AssessmentInviteData';
import { useNavigate } from 'react-router-dom';
import { TagFormInput } from '../common/TagFormInput';
import { Tag } from 'react-tag-autocomplete';


type RequestUpdateScanProps = {
    routes: [AssessmentRoute] | []
};

export interface SelectedAssessmentData {
    uuid: string;
    id: number
    venture?: string ;
    founder?: string | null;
    founderEmailAddress?: string | null;
    scanType?: string | null;
    completionTime?: string ;
    organisation: {
        contact?: {
            emailAddress?: string
        }
    }
}

const validationSchema = yup.object().shape({
    name: yup.string().required(),
    emailAddress: yup.string().email().required(),
    venture: yup.string().required(),
    language: yup.string().oneOf(['nl', 'en'])
});

const letterNL = `
Beste [ondernemer],

Wij zijn benieuwd welke groeistap je met [venture] hebt gemaakt. Daarom nodigen we je uit om jouw bedrijfsanalyse te updaten door de vragenlijst aan te vullen met recente ontwikkelingen. Dat gaat heel makkelijk want wij hebben de antwoorden van jouw vorige bedrijfsanalyse al klaar staan voor een review.

* Review de vragenlijst

Na afloop van de review sturen wij een nieuw bedrijfsrapport met daarin de huidige levensfase, mijlpaal en de consistentie waarmee [venture] groeit.

Mocht je vragen hebben, schroom dan niet om contact met ons op te nemen op [email begeleider].

Hartelijke groet,

[begeleider]

`;

const letterEN = `Dear [ondernemer], 

We are curious about what growth step you have made with [venture]. That is why we invite you to update your business analysis by supplementing the questionnaire with recent developments. This is very easy because we already have the answers from your previous business analysis ready for a review.

* Review the questionnaire

In the ProgressScan©, previously given answers have already been entered in advance. These answers come from a completed Scan© on [old_assessment_finished_date]. We would like to ask you to take a good look at these answers and to adjust them if changes have taken place.

After the review, we will send a new company report containing the current life stage, milestone and the consistency with which [venture] is growing.

Sincerely,

[begeleider]`;

export const RequestProgressScanForm = ({
    routes
}: RequestUpdateScanProps) => {
    const initialFormValues = {
        name: "",
        emailAddress: "",
        venture: "",
        language: "nl",
        assessmentRoute: null,
        assessmentId: null,
        tags: [] = []
    };
    const [selectedAssessment, setSelectedAssessment] = useState<String | Number | null>(null);
    const [selectedAssessmentUuid, setSelectedAssessmentUuid] = useState<String | null>(null);
    const [selectedAssessmentData, setSelectedAssessmentData] = useState<SelectedAssessmentData|null>(null);
    const [selectObject, setSelectObject] = useState<{label:String, value: String|Number} | null>(null);
    const [options, setOptions] = useState<[{label:String, value: String|Number}] | []>([]);
    const formikRef = useRef<FormikProps<RequestUpdateScanProps>>(null);
    const [message, setMessage] = useState<String | null>(null);
    const [nameValidation, setNameValidation] = useState(false);
    const [formData, setFormData] = useState<RequestAssessmentUpdateData>(initialFormValues)
    const [sendingSuccessful, setSendingSuccessful] = useState(false)
    const [errorFound, setErrorFound] = useState(false)
    const [formValid, setFormValid] = useState(false)
    const [tags, setTags] = useState<[Tag] | []>([])
    const [tagSuggestions, setTagSuggestions] = useState([]);
    const userContext = useContext(UserContext)
    const navigate = useNavigate();

    useQuery(TAG_SUGGESTIONS, {
        onCompleted: (someData) => {
            setTagSuggestions(someData.tagSuggestions)
        },
        fetchPolicy: "no-cache"
    });

    useQuery(ASSESSMENTS_MINIMAL, {
        variables: {
            noChildAssessments: true
        },
        onCompleted: (someData) => {
            // @ts-ignore
            let subset = someData.assessmentsMinimal.map((assessment) => {
                return {
                    label: `${moment(assessment.completionTime).format('YYYY-MM-DD HH:mm')} - ${assessment.venture} (${assessment.scanType})`,
                    value: assessment.id
                }
            })
            subset = subset.reverse()
            setOptions(subset)
        },
        fetchPolicy: "no-cache"
    });

    const [getAssessmentData, { loading, client, fetchMore }] = useLazyQuery(ASSESSMENT_BY_ID, {
            onCompleted: (someData) => {
                initialFormValues.name = someData.assessment.founder;
                initialFormValues.emailAddress = someData.assessment.founderEmailAddress ?? someData.assessment.organisation.contact.emailAddress;
                setSelectedAssessmentUuid(someData.assessment.uuid)
                setSelectedAssessmentData(someData.assessment)
                setFormData({
                    ...initialFormValues,
                    ...someData.assessment
                })
                setSelectedAssessment(someData.assessment.id)
                setFormValid(false)
            }
        });

    const [sendScanUpdateAction] = useMutation(SEND_ASSESSMENT_UPDATE, {
        onCompleted: (data) => {
            if(data !== null) {
                if(data.sendUpdateInvite.code === 200) {
                    setSendingSuccessful(true)
                    setErrorFound(false)
                    setTimeout(() => {
                        formikRef.current?.resetForm()
                        setFormData(initialFormValues)
                        setSelectedAssessmentData(null)
                        setFormValid(false)
                        setSendingSuccessful(false)
                        navigate('/library/startups?tab=invites')
                    }, 1500)
                } else {
                    setErrorFound(true)
                    setTimeout(() => {
                        setSendingSuccessful(false)
                        setErrorFound(false)
                    }, 2500)
                }
            }
        },
        onError: () => {
            setErrorFound(true)
            setTimeout(() => {
                setSendingSuccessful(false)
                setErrorFound(false)
            }, 2500)
        }
    });

    const sendUpdate = async (formData: AssessmentInviteData, message: String) => {
        sendScanUpdateAction({
            variables: {
                name: formData.name,
                emailAddress: formData.emailAddress,
                venture: formData.venture,
                language: formData.language,
                message,
                assessmentRoute: formData.assessmentRoute,
                assessmentId: selectedAssessment,
                tags: formData.tags
            },
        })
    };

    const submitForm = ( values: {
         name: string,
         emailAddress: string,
         venture: string,
         language: string
         assessmentRoute: number | null
     },
    ) => {
        const { name, emailAddress, venture, language, assessmentRoute } = values;

        let letter = ''
        if(language === 'nl') {
            letter = letterNL
        } else {
            letter = letterEN
        }

        let chosenAssessmentRoute = null
        let chosenAssessmentName = 'ConsistencyScan'
        if(routes && routes.length) {
            if(assessmentRoute === null) {
                chosenAssessmentRoute = routes[0].id
            } else {
                // @ts-ignore
                let choosenRoute = routes.filter(o => o.id === parseInt(assessmentRoute))
                chosenAssessmentRoute = choosenRoute[0].id
                chosenAssessmentName = choosenRoute[0].name
            }
        }
        if(assessmentRoute === null && routes.length) {
            chosenAssessmentRoute = routes[0].id
        }
        let filledInLetter = letter.replace('[ondernemer]', name.split(" ")[0])
        filledInLetter = filledInLetter.replaceAll('[venture]', venture)
        filledInLetter = filledInLetter.replace('[email begeleider]', userContext.emailAddress)
        filledInLetter = filledInLetter.replace('[begeleider]', userContext.name)
        filledInLetter = filledInLetter.replaceAll('[assessment_name]', 'ProgressScan')
        const completionDate = selectedAssessmentData?.completionTime !== null ? moment(selectedAssessmentData?.completionTime).format('DD-MM-YYYY HH:mm') : '';
        filledInLetter = filledInLetter.replace('[old_assessment_finished_date]', completionDate)

        setMessage(filledInLetter)
        setFormValid(!formValid)
        setFormData({
            name,
            venture,
            emailAddress,
            language,
            assessmentRoute: chosenAssessmentRoute,
            assessmentId: selectedAssessment,
            tags,
        })
    }

    return (
        <div className={styles.wrapper}>
            <section className={styles.assessmentSelect}>
                { options.length > 0 &&
                    <Select options={options} placeholder={'Select a scan'} className="basic-single" value={selectObject}  classNamePrefix="select" onChange={
                        // @ts-ignore
                        async (e) => {
                            setSelectObject(e)
                            if(e?.value !== undefined) {
                                await getAssessmentData({
                                    variables: {
                                        // @ts-ignore
                                        assessmentId: typeof e.value === 'number' ? e.value : parseInt(e.value)
                                    }
                                })
                            }
                        }
                    }/>
                }
                { selectedAssessmentUuid !== null && (
                    <div>
                        <br/>
                        <p className={styles.selectedAssessmentText}>Selected assessment: </p>
                        <ul>
                            {userContext?.organisation?.id === 1 && (
                                <li><span className={styles.listItem}>UUID:</span>{selectedAssessmentUuid}</li>
                            )}
                            <li><span className={styles.listItem}>Company:</span>{selectedAssessmentData?.venture}</li>
                            <li><span className={styles.listItem}>Completion date:</span> {moment(selectedAssessmentData?.completionTime).format('YYYY-MM-DD HH:mm')}</li>
                        </ul>
                    </div>
                )}
            </section>
            {!formValid && selectedAssessment && (
                <section>
                    <div className={styles.header}>
                        <p className={styles.name}>Update details</p>
                    </div>
                    <Formik
                        enableReinitialize
                        validationSchema={validationSchema}
                        initialValues={formData}
                        onSubmit={submitForm}
                        // @ts-ignore
                        innerRef={formikRef}
                    >
                        {({ values, errors, touched, handleChange, handleSubmit, handleBlur, isValid }) => (
                            <form className={styles.formContainer} onSubmit={handleSubmit}>
                                <div className={styles.inputContainer}>
                                    {errors.name && touched.name ? (
                                        <span className={styles.error}>Name is required</span>
                                    ) : null}
                                    <Field
                                        id="assessment-invite-name"
                                        name='name'
                                        as={Input}
                                        type='text'
                                        placeholder='Name'
                                        onChange={handleChange}
                                        value={values.name}
                                        autoFocus
                                        onBlur={(e: React.FocusEvent<any>) => {
                                            if(nameValidation) {
                                                handleBlur(e)
                                            }
                                        }}
                                    />
                                </div>
                                <div className={styles.inputContainer}>
                                    {errors.emailAddress && touched.emailAddress ? (
                                        errors.emailAddress.includes('valid')
                                            ? <span className={styles.error}>Email format is incorrect</span>
                                            : <span className={styles.error}>Email address is required</span>
                                    ) : null}
                                    <Field
                                        name='emailAddress'
                                        as={Input}
                                        type='text'
                                        placeholder='Email address'
                                        onChange={handleChange}
                                        value={values.emailAddress}
                                    />
                                </div>
                                <div className={styles.inputContainer}>
                                    {errors.venture && touched.venture ? (
                                        <span className={styles.error}>Company name is required</span>
                                    ) : null}
                                    <Field
                                        name='venture'
                                        as={Input}
                                        type='text'
                                        placeholder='Company name'
                                        onChange={handleChange}
                                        value={values.venture}
                                    />
                                </div>
                                <div className={styles.inputContainer}>
                                    <TagFormInput suggestions={tagSuggestions} tags={tags} setTags={(tags: [Tag]) => {
                                        setTags(tags)
                                    }}/>
                                </div>
                                <div className={styles.selectContainer}>
                                    <Field
                                        className={styles.select}
                                        name='language'
                                        as="select"
                                        placeholder='Language'
                                        onChange={handleChange}
                                        value={values.language}
                                    >
                                        <option value="nl">Dutch</option>
                                        <option value="en">English</option>
                                    </Field>
                                </div>
                                {routes && routes.length > 1 && (
                                    <div className={styles.selectContainer}>
                                        <Field
                                            className={styles.select}
                                            name='assessmentRoute'
                                            as="select"
                                            placeholder='Assessment'
                                            onChange={handleChange}
                                        >
                                            {
                                                routes.map((el) => (
                                                    <option value={el.id}>{el.name}</option>
                                                ))
                                            }
                                        </Field>
                                    </div>
                                )}
                                <ButtonPageLayout type='submit' disabled={!isValid}>Next</ButtonPageLayout>
                            </form>
                        )}
                    </Formik>
                </section>
            )}
            {formValid && message !== null && (
                <section>
          <textarea
              className={styles.textField}
              value={typeof message === 'string' ? message : '' }
              onChange={(e) => setMessage(e.target.value)}
          ></textarea>
                    <section>
                        <button
                            className={styles.send}
                            onClick={() => {
                                sendUpdate(formData, message)
                            }}
                        >{`Send`}</button>
                    </section>
                    {sendingSuccessful && (
                        <span className={styles.sendSuccessful}>Mail sent succesfully</span>
                    )}
                    {errorFound && (
                        <span className={styles.sendFailed}>Oops, something went wrong with sending the invite, don't worry, we're notified of the issue</span>
                    )}
                </section>
            )}
        </div>
    )
}
